import type { DeleteThirdPartyMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * The tRPC delete returns void and throws if no rows are affected, so we
 * hardcode a success-shaped response mirroring the GraphQL mutation structure.
 * Only delete_third_party_by_pk.Id is meaningful (the deleted id).
 */
const mapTrpcResponseToGraphQL = (id: string): DeleteThirdPartyMutation => {
  return {
    delete_questionnaire_invite: {
      __typename: 'questionnaire_invite_mutation_response',
      affected_rows: 0,
    },
    delete_third_party_response: {
      __typename: 'third_party_response_mutation_response',
      affected_rows: 0,
    },
    delete_third_party_by_pk: {
      __typename: 'third_party',
      Id: id,
    },
  };
};

export const useDeleteThirdPartyTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.thirdParty.delete.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.thirdParty.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.thirdParty.getById.queryKey(),
        });
      },
    }),
  });

  return {
    deleteThirdParty: async (id: string): Promise<DeleteThirdPartyMutation> => {
      await mutation.mutateAsync({ id });

      return mapTrpcResponseToGraphQL(id);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
